/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 批量编辑水印页面图片预览组件
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-01-28 09:44:43
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { message, Modal } from 'antd'
import {
  useWatermarkBatchContext,
  WatermarkExtractionStatus,
  PhotoItem
} from '../../context/WatermarkBatchContext'
import { useWatermarkRemoval } from '../../hooks/useWatermarkRemoval'
import { useImageDownload } from '../../hooks/useImageDownload'
import FailedOverlay from './FailedOverlay'
import BottomToolbar from './BottomToolbar'
import DownloadEditedPhotosModal from '../Download/DownloadEditedPhotosModal'
import './ImagePreview.scss'
import {
  usePayment,
  useVip,
  useAppDispatch,
  useGroupID,
  useAppSelector,
  useUserId
} from '../../../../../app/hooks'
import { setPhotoPickerPhotos } from '../../../../../actions/photoPicker'
import { folderDetailPath } from '../../../../../utils/aiFolderClassify'
import { removeWatermarkDownload } from '../../../../../actions/photo'
import { uploadPhotosToFolders } from '../../../../../actions/folder'
import {
  uploadToOSS,
  uploadToTencentCOS,
  fetchConfig,
  fetchUploadToken
} from '../../../../../actions/main'
import { uploadPhoto } from '../../../../../actions/upload'
import { serverSideDecode } from '../../../../../actions/watermark'
import {
  notTodayCameraShotUserComment,
  readExif,
  ExifInfo
} from '../../../../components/UploadPhotos/lib'
import { v4 as uuid } from 'uuid'
import CONFIG from '../../../../../constants/config'
import dayjs from 'dayjs'
import { produce } from 'immer'
import {
  useWatermarkBatchEditorTrack,
  calculatePhotoStats
} from '../../hooks/useWatermarkBatchEditorTrack'
import { useWatermarkTimesLimit } from '../../hooks/useWatermarkTimesLimit'
import { calculateEditableItemCount } from '../../hooks/useWatermarkItemEditStatus'
import useImageProcessUrl from '../../hooks/useImageProcessUrl'
import { LoadingOutlined } from '@ant-design/icons'

declare const __RELEASE_STAGE__: string

const ImagePreview: React.FC = () => {
  const {
    currentPhoto,
    photoList: photos,
    watermarkCnt,
    decrementWatermarkCnt,
    showSurveyModal,
    setPhotos,
    currentIndex,
    setCurrentIndex,
    setUploadModalVisible,
    setDownloadProgress,
    registerUploadConfirmHandler,
    registerDownloadAllHandler,
    updatePhotosStatus
  } = useWatermarkBatchContext()
  const navigate = useNavigate()
  // 当有aiURL时默认显示编辑后的图片（对比模式关闭）
  const [compareBefore, setCompareBefore] = useState(false)
  // 下载全部照片的加载状态（防止重复点击）
  const [downloadingAll, setDownloadingAll] = useState(false)
  // 下载弹窗显示状态
  const [downloadModalVisible, setDownloadModalVisible] = useState(false)
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  const { buildProcessedUrl } = useImageProcessUrl()

  const vipInfo = useVip()
  const payment = usePayment()
  const dispatch = useAppDispatch()
  const groupID = useGroupID()
  const userId = useUserId()
  const inviteID = useAppSelector((state) => state.group.invite_id)
  const ossToken = useAppSelector((state) => state.main.ossToken)

  // 使用去水印hook
  const { removeWatermarks, removeWatermarksAsList } = useWatermarkRemoval()

  // 上传相关状态
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 })
  const ossServiceConfigRef = useRef<any>(null)

  // 获取OSS配置
  const getOssServiceConfig = async () => {
    if (ossServiceConfigRef.current) {
      return ossServiceConfigRef.current
    }
    try {
      const res = await dispatch(
        fetchConfig({
          names: CONFIG.KVParams.names
        })
      )
      if (res?.items) {
        const configItem = res.items.find(
          (item) => item.name === 'oss_service_config'
        )
        if (configItem?.value) {
          try {
            const parsedConfig = JSON.parse(configItem.value)
            ossServiceConfigRef.current = parsedConfig
            return parsedConfig
          } catch (parseError) {
            console.error('OSS配置解析失败:', parseError)
          }
        }
      }
      return { photo: { default_oss_service: 'ali' } }
    } catch (error) {
      console.error('获取OSS配置失败:', error)
      return { photo: { default_oss_service: 'ali' } }
    }
  }

  // 获取OSS上传方式
  const getOssUploadWay = (config: any) => {
    if (
      config.oss_disable_qcloud == 1 ||
      !config.oss_service_traffic_ratio ||
      typeof config.oss_service_traffic_ratio !== 'string'
    ) {
      return config.default_oss_service || 'ali'
    } else {
      try {
        const ratioConfig = JSON.parse(config.oss_service_traffic_ratio)
        if (ratioConfig.ali + ratioConfig.tencent > 100) {
          return config.default_oss_service || 'ali'
        }
        return (ratioConfig.ali + ratioConfig.tencent) * Math.random() <=
          ratioConfig.ali
          ? 'ali'
          : 'tencent'
      } catch (error) {
        return config.default_oss_service || 'ali'
      }
    }
  }

  // 获取有效的 OSS Token（过期则刷新）
  const getValidOssToken = async () => {
    const bucketName = 'net-cloud'
    const token = ossToken
    // 检查 token 是否存在且未过期（预留 60 秒余量）
    if (
      !token?.[bucketName]?.['accessid'] ||
      !token?.[bucketName]?.['expire'] ||
      token[bucketName]['expire'] < Date.now() / 1000 + 60
    ) {
      console.warn('[uploadToOSSAction] OSS Token 过期或缺失，刷新中...')
      const newToken = await dispatch(fetchUploadToken())
      if (newToken) {
        return newToken
      }
      throw new Error('刷新 OSS Token 失败')
    }
    return token
  }

  // 上传文件到OSS（带重试）
  const uploadToOSSAction = async (
    file: File,
    maxRetries = 2
  ): Promise<string> => {
    const bucketName = 'net-cloud'
    const filename = uuid() + '.' + (file.name.split('.').pop() || 'jpg')
    const ossServiceConfig = await getOssServiceConfig()
    const ossPhotoConfig = ossServiceConfig?.photo
    const way = getOssUploadWay(ossPhotoConfig)

    let lastError: any = null
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        if (way === 'ali') {
          // 每次重试都获取最新 token
          const token = await getValidOssToken()
          await uploadToOSS({
            key: `group/photo/${filename}`,
            OSSAccessKeyId: token?.[bucketName]?.['accessid'],
            policy: token?.[bucketName]?.['policy'],
            Signature: token?.[bucketName]?.['signature'],
            file: file
          })
          return filename
        } else {
          await uploadToTencentCOS({
            file: file,
            key: `group/photo/tencent-${filename}`
          })
          return `tencent-${filename}`
        }
      } catch (err) {
        const error = err as any
        lastError = error
        console.error(
          `[uploadToOSSAction] 第${attempt + 1}次上传失败:`,
          error?.message || error
        )
        if (attempt < maxRetries) {
          // 如果是 token 过期（403），刷新 token 后重试
          if (
            error?.status === 403 ||
            /expired|signature/i.test(error?.body || '')
          ) {
            console.warn('[uploadToOSSAction] 疑似 Token 过期，刷新后重试')
            await dispatch(fetchUploadToken())
          }
          // 等待后重试
          await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)))
        }
      }
    }
    return Promise.reject(lastError || new Error('上传到OSS失败'))
  }

  // 添加照片到文件夹
  const addPhotoToFolderAction = async (photoID: number, folderID: number) => {
    try {
      const data = await dispatch(
        uploadPhotosToFolders({
          groupID,
          folderIDs: [folderID],
          photoIDs: [String(photoID)],
          source: 7
        })
      )
      return data.limitFolderIDs.length === 0
    } catch (error) {
      console.error('添加照片到文件夹失败:', error)
      return false
    }
  }

  // 解码用户评论
  const getUserCommentDecode = async (userComment = '') => {
    if (userComment) {
      return dispatch(serverSideDecode({ data: userComment }))
    }
    return ''
  }

  // 从URL下载图片并转换为File对象

  // 处理上传已编辑照片
  const handleUploadEditedPhotos = async (
    selectedPhotos: PhotoItem[],
    folderID: number,
    folderName?: string,
    options?: { suppressToast?: boolean }
  ): Promise<PhotoItem[]> => {
    if (uploading || selectedPhotos.length === 0 || !folderID) {
      return []
    }

    setUploading(true)
    setUploadProgress({ current: 0, total: selectedPhotos.length })
    setDownloadProgress({
      visible: true,
      current: 0,
      total: selectedPhotos.length,
      title: '正在上传照片到文件夹'
    })

    const uploadedPhotos: PhotoItem[] = []

    try {
      // 1. 获取已编辑照片的URL列表（使用aiURL）
      const photoUrls = selectedPhotos
        .map((photo) => photo.aiURL)
        .filter(Boolean)

      if (photoUrls.length === 0) {
        message.warning('没有可上传的照片')
        setUploading(false)
        setDownloadProgress({ visible: false, current: 0, total: 0 })
        return []
      }

      // 2. 调用去水印接口获取List格式（JSON List String）
      const removalResults = await removeWatermarksAsList(
        photoUrls,
        (current, total) => {
          // 更新去水印进度（占总进度的30%）
          const cur = Math.floor(
            (current / total) * 0.3 * selectedPhotos.length
          )
          setUploadProgress({
            current: cur,
            total: selectedPhotos.length
          })
          setDownloadProgress({
            visible: true,
            current: cur,
            total: selectedPhotos.length,
            title: '正在上传照片到文件夹'
          })
        }
      )

      if (!removalResults || removalResults.length === 0) {
        message.error('获取去水印照片失败')
        setUploading(false)
        setDownloadProgress({ visible: false, current: 0, total: 0 })
        return []
      }

      // 3. 创建原始URL到去水印后URL的映射
      const urlMapping = new Map<string, string>()
      removalResults.forEach((item) => {
        if (item.success === 1 && item.res) {
          // 成功：使用去水印后的URL
          urlMapping.set(item.src, item.res)
        } else {
          // 失败：使用原始URL
          urlMapping.set(item.src, item.src)
        }
      })

      // 4. 上传每个图片文件
      const failedPhotos: Array<{
        photo: PhotoItem
        processedUrl: string
        reason: string
      }> = []

      for (let index = 0; index < selectedPhotos.length; index++) {
        const photo = selectedPhotos[index]
        const originalUrl = photo.aiURL
        if (!originalUrl) {
          failedPhotos.push({
            photo,
            processedUrl: '',
            reason: '缺少编辑后的图片'
          })
          continue
        }

        // 获取去水印后的URL
        const processedUrl = urlMapping.get(originalUrl) || originalUrl

        // 从URL下载图片并转换为File对象
        let file: File
        try {
          const response = await fetch(processedUrl)
          if (!response.ok) {
            throw new Error(`下载图片失败: ${response.statusText}`)
          }
          const blob = await response.blob()
          const urlPath = processedUrl.split('/').pop() || 'photo.jpg'
          const fileName = urlPath.split('?')[0] || 'photo.jpg'
          file = new File([blob], fileName, {
            type: blob.type || 'image/jpeg'
          })
        } catch (error) {
          console.error(`下载第${index + 1}张照片失败:`, error)
          failedPhotos.push({
            photo,
            processedUrl,
            reason: '下载图片失败'
          })
          continue
        }

        // 上传到OSS
        let filename: string
        try {
          filename = await uploadToOSSAction(file)
          console.log(`第${index + 1}张照片上传到OSS成功，filename:`, filename)
        } catch (error) {
          console.error(`第${index + 1}张照片上传到OSS失败:`, error)
          failedPhotos.push({
            photo,
            processedUrl,
            reason: '上传到云存储失败'
          })
          continue
        }

        // 读取EXIF信息
        const isJPG = file.type === 'image/jpeg' || file.type === 'image/jpg'
        const exifInfo: ExifInfo = isJPG ? await readExif(file) : {}
        const userComment = await getUserCommentDecode(exifInfo?.userComment)

        const imageInfo: any = { fileSize: file.size }
        if (isJPG) {
          imageInfo.width = exifInfo?.width
          imageInfo.height = exifInfo?.height
        }

        const params: any = {
          user_id: userId || '',
          group_ids: [groupID],
          file_name: filename,
          time: dayjs().format('YYYY-MM-DD HH:mm:ss'),
          mediaType: 0, // 0表示图片
          imageInfo
        }

        // 处理 userComment（参考 UploadDialog.jsx 的实现）
        const parsedUserComment = userComment
          ? JSON.parse(userComment)
          : notTodayCameraShotUserComment // 非今拍照片or视频，无法读取exif信息

        // 今拍照片
        if (parsedUserComment?.data) {
          const newUserComment = produce(parsedUserComment, (draft: any) => {
            draft.data.fileName = filename
          }) as any
          params.userComment = newUserComment
          params.location = newUserComment?.data?.baseInfo?.location
          params.lat = Number(newUserComment?.data?.baseInfo?.latitude)
          params.lng = Number(newUserComment?.data?.baseInfo?.longitude)
          params.time = newUserComment?.data?.baseInfo?.time
          params.sourceType = 0
        }
        // 非今拍or视频，无法读取exif信息, 但必须提交以下userComment内容
        if (parsedUserComment?.data2) {
          const newUserComment = produce(parsedUserComment, (draft: any) => {
            draft.data2.fileName = filename
            draft.data2.userID = userId || ''
          }) as any
          params.userComment = newUserComment
          params.sourceType = 1
        }

        // 创建照片记录
        let photoID: number | null = null
        try {
          const uploadResult = await dispatch(uploadPhoto(params))
          console.log(
            `第${index + 1}张照片 uploadPhoto 返回结果:`,
            uploadResult
          )

          // uploadPhoto 返回格式：{ status: 0, photoIDMap: { [inviteID]: photoID } }
          if (uploadResult?.status === 0 && uploadResult?.photoIDMap) {
            photoID = uploadResult.photoIDMap[Number(inviteID)] || null
            console.log(
              `第${index + 1}张照片 photoID:`,
              photoID,
              'inviteID:',
              inviteID
            )
          } else {
            console.error(
              `第${index + 1}张照片 uploadPhoto 返回格式错误:`,
              uploadResult
            )
            failedPhotos.push({
              photo,
              processedUrl,
              reason: '创建照片记录失败'
            })
            continue
          }
        } catch (error) {
          console.error(`第${index + 1}张照片创建照片记录失败:`, error)
          failedPhotos.push({
            photo,
            processedUrl,
            reason: '创建照片记录失败'
          })
          continue
        }

        if (!photoID) {
          console.error(`第${index + 1}张照片 photoID 为空，跳过添加到文件夹`)
          failedPhotos.push({
            photo,
            processedUrl,
            reason: '获取照片ID失败'
          })
          continue
        }

        // 添加到文件夹
        try {
          const success = await addPhotoToFolderAction(photoID, folderID)
          console.log(
            `第${index + 1}张照片添加到文件夹结果:`,
            success,
            'photoID:',
            photoID,
            'folderID:',
            folderID
          )
          if (!success) {
            failedPhotos.push({
              photo,
              processedUrl,
              reason: '添加到文件夹失败，可能超过数量限制'
            })
            break
          }
        } catch (error) {
          console.error(`第${index + 1}张照片添加到文件夹失败:`, error)
          failedPhotos.push({
            photo,
            processedUrl,
            reason: '添加到文件夹失败'
          })
          continue
        }

        uploadedPhotos.push(photo)

        // 更新进度（占总进度的70%，从30%开始）
        const cur = Math.floor(
          0.3 * selectedPhotos.length +
            ((index + 1) / selectedPhotos.length) * 0.7 * selectedPhotos.length
        )
        setUploadProgress({
          current: cur,
          total: selectedPhotos.length
        })
        setDownloadProgress({
          visible: true,
          current: cur,
          total: selectedPhotos.length,
          title: '正在上传照片到文件夹'
        })
      }

      const uploadedCount = uploadedPhotos.length
      const failedCount = failedPhotos.length

      setDownloadProgress({ visible: false, current: 0, total: 0 })
      setUploadModalVisible(false)

      // 有失败照片时弹窗提示，支持下载（suppressToast 时由调用方自行处理提示）
      if (failedCount > 0 && !options?.suppressToast) {
        const handleDownloadFailed = async () => {
          setDownloadProgress({
            visible: true,
            current: 0,
            total: failedCount,
            title: '正在下载失败照片到本地'
          })
          let downloadedCount = 0
          for (const item of failedPhotos) {
            const url = item.processedUrl || item.photo.aiURL || ''
            if (!url) continue
            try {
              const resp = await fetch(url)
              if (!resp.ok) continue
              const blob = await resp.blob()
              const blobUrl = window.URL.createObjectURL(blob)
              const link = document.createElement('a')
              const name =
                (item.photo.smallURL || item.photo.url || 'photo.jpg')
                  .split('/')
                  .pop()
                  ?.split('?')[0] || 'photo.jpg'
              link.href = blobUrl
              link.download = name
              link.style.display = 'none'
              document.body.appendChild(link)
              link.click()
              document.body.removeChild(link)
              window.URL.revokeObjectURL(blobUrl)
              downloadedCount++
              setDownloadProgress({
                visible: true,
                current: downloadedCount,
                total: failedCount,
                title: '正在下载失败照片到本地'
              })
            } catch (e) {
              console.error('下载失败照片异常:', e)
            }
          }
          setDownloadProgress({ visible: false, current: 0, total: 0 })
          if (downloadedCount > 0) {
            message.success(`已下载 ${downloadedCount} 张照片到本地`)
          }
        }

        Modal.warning({
          title: `${failedCount}张照片上传失败`,
          width: 480,
          content: (
            <div>
              {uploadedCount > 0 && (
                <p>
                  成功上传 {uploadedCount} 张到
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(folderDetailPath(folderID))}
                    style={{
                      cursor: 'pointer',
                      color: '#1890ff',
                      textDecoration: 'underline',
                      marginLeft: 4
                    }}
                  >
                    {folderName || '文件夹'}
                  </span>
                  ，以下照片上传失败：
                </p>
              )}
              <div
                style={{
                  maxHeight: 200,
                  overflowY: 'auto',
                  margin: '8px 0'
                }}
              >
                {failedPhotos.map((item, i) => (
                  <div
                    key={item.photo.seq}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      marginBottom: 8
                    }}
                  >
                    <img
                      src={
                        item.photo.aiURL ||
                        item.photo.smallURL ||
                        item.photo.url
                      }
                      alt={`失败照片${i + 1}`}
                      style={{
                        width: 48,
                        height: 48,
                        objectFit: 'cover',
                        borderRadius: 4,
                        flexShrink: 0
                      }}
                    />
                    <span style={{ color: '#ff4d4f', fontSize: 13 }}>
                      {item.reason}
                    </span>
                  </div>
                ))}
              </div>
              <p style={{ color: '#666', fontSize: 13, marginTop: 8 }}>
                你可以将失败的照片下载到本地保存。
              </p>
            </div>
          ),
          okText: `下载${failedCount}张失败照片`,
          onOk: handleDownloadFailed
        })
      } else if (!options?.suppressToast) {
        message.success({
          content: (
            <span>
              成功上传{uploadedCount}张照片到
              <span
                role="button"
                tabIndex={0}
                onClick={() => navigate(folderDetailPath(folderID))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    navigate(folderDetailPath(folderID))
                  }
                }}
                style={{
                  cursor: 'pointer',
                  color: '#1890ff',
                  textDecoration: 'underline',
                  marginLeft: 4
                }}
              >
                {folderName || '文件夹'}
              </span>
            </span>
          ),
          duration: 4
        })
      }

      // 记录去水印次数（在更新状态前，只记录未下载的照片）
      const photosWithStatus = selectedPhotos.map((photo) => ({
        url: photo.aiURL || photo.smallURL || photo.url,
        isDownloaded: photo.isDownloaded
      }))
      const notDownloadedCount = photosWithStatus.filter(
        (photo) => !photo.isDownloaded
      ).length

      if (notDownloadedCount > 0 && groupID) {
        try {
          await dispatch(
            removeWatermarkDownload({
              groupID: groupID,
              removeType: 1, // 1 表示 ai去水印
              watermarkCount: notDownloadedCount
            })
          )
        } catch (error) {
          console.error('记录去水印次数失败:', error)
        }
      }

      // 更新所有成功上传照片的下载状态（标记为已下载，避免返回时拦截）
      const photosToUpdate = selectedPhotos.map((photo) => ({
        ...photo,
        isDownloaded: true
      }))
      // 按状态分组，确保每张照片使用其当前状态进行更新
      const photosByStatus = new Map<WatermarkExtractionStatus, PhotoItem[]>()
      photosToUpdate.forEach((photo) => {
        const status =
          photo.watermarkStatus || WatermarkExtractionStatus.EDIT_COMPLETED
        if (!photosByStatus.has(status)) {
          photosByStatus.set(status, [])
        }
        photosByStatus.get(status)!.push(photo)
      })
      // 按状态分组批量更新
      photosByStatus.forEach((photos, status) => {
        updatePhotosStatus(photos, status)
      })
      return uploadedPhotos
    } catch (error) {
      console.error('上传照片失败:', error)
      message.error('上传照片失败，请稍后重试')
      return []
    } finally {
      setUploading(false)
      setUploadProgress({ current: 0, total: 0 })
      setDownloadProgress({ visible: false, current: 0, total: 0 })
    }
  }

  // 向上文注册上传确认回调，供根 index 在上传弹窗 onConfirm 时调用
  useEffect(() => {
    registerUploadConfirmHandler(handleUploadEditedPhotos)
    return () => registerUploadConfirmHandler(null)
  }, [handleUploadEditedPhotos, registerUploadConfirmHandler])

  // 使用图片下载hook
  const { downloadImage, downloadImages } = useImageDownload()

  // 使用埋点 hook
  const { trackWatermarkBatchEditor } = useWatermarkBatchEditorTrack()

  // 使用次数不足提示 hook
  const { handleTimesLimit: handleBatchTimesLimit } =
    useWatermarkTimesLimit('clickDownloadAll')
  const { handleTimesLimit: handleSingleTimesLimit } = useWatermarkTimesLimit(
    'clickDownloadCurrentPhoto'
  )

  // 计算照片统计
  const { allPhotoCount, uneditedCount, failedExtractionCount } =
    calculatePhotoStats(photos)

  // 已处理完成的照片列表（编辑成功 + 无修改项 + 超时）
  const processedPhotoList = useMemo(() => {
    return photos.filter(
      (photo) =>
        photo.watermarkStatus === WatermarkExtractionStatus.EDIT_COMPLETED ||
        photo.watermarkStatus === WatermarkExtractionStatus.EDIT_NO_CHANGES ||
        photo.watermarkStatus === WatermarkExtractionStatus.EDIT_TIMEOUT
    )
  }, [photos])
  const processedPhotosCount = processedPhotoList.length

  // 当照片切换时，更新对比开关状态
  useEffect(() => {
    // 如果有aiURL，默认关闭对比（显示编辑后的图片）
    // 如果没有aiURL，默认关闭对比（显示原图）
    setCompareBefore(false)
  }, [currentPhoto])

  const handleDownloadAll = async (): Promise<void> => {
    // 如果正在下载，防止重复点击
    if (downloadingAll) {
      return
    }

    if (processedPhotosCount === 0) {
      message.warning('没有已编辑的照片可下载')
      return
    }

    // 设置下载状态为 true
    setDownloadingAll(true)

    // 埋点：点击下载全部
    trackWatermarkBatchEditor({
      actionType: 'clickDownloadAll',
      number1: watermarkCnt,
      number2: allPhotoCount,
      number3: uneditedCount,
      number4: failedExtractionCount,
      number5: processedPhotosCount, // 要下载的照片数（处理成功照片数）
      photoNum: allPhotoCount
    })

    if (!vipInfo.isVip) {
      payment.open('webBatchEditWatermarkOnceCnt')
      // 如果不是 VIP，重置状态（虽然不会执行下载，但保持状态一致）
      setDownloadingAll(false)
      return
    }

    // VIP 用户继续执行下载逻辑
    if (watermarkCnt < processedPhotosCount) {
      handleBatchTimesLimit(
        `编辑次数不足，需要${processedPhotosCount}次，当前剩余${watermarkCnt}次`
      )
      // 次数不足时重置状态
      setDownloadingAll(false)
      return
    }

    // 计算已编辑数量（编辑完成的照片）
    const editedCount = photos.filter(
      (photo) =>
        photo.watermarkStatus === WatermarkExtractionStatus.EDIT_COMPLETED
    ).length

    // 如果已编辑数量大于200，提供下载选项
    if (editedCount > 200) {
      Modal.confirm({
        title: '提示',
        content: (
          <div>
            <p>
              你当前已编辑数量为{editedCount}
              张。为了避免异常报错，建议您下载完成后清空已编辑的照片列表。
            </p>
          </div>
        ),
        okText: '下载并移出照片列表',
        cancelText: '仅下载',
        onOk: () => {
          // 用户选择下载并移出照片列表
          executeDownload(processedPhotoList, true)
        },
        onCancel: () => {
          // 用户选择仅下载
          executeDownload(processedPhotoList, false)
        }
      })
      return
    }

    // 已编辑数量未超过200张，直接执行下载
    executeDownload(processedPhotoList, false)
  }

  const handleDownloadSelected = async (
    selectedPhotos: PhotoItem[]
  ): Promise<void> => {
    if (downloadingAll) {
      return
    }

    if (selectedPhotos.length === 0) {
      message.warning('请选择要下载的照片')
      return
    }

    setDownloadingAll(true)

    // 埋点：点击下载全部（使用所选数量）
    trackWatermarkBatchEditor({
      actionType: 'clickDownloadAll',
      number1: watermarkCnt,
      number2: allPhotoCount,
      number3: uneditedCount,
      number4: failedExtractionCount,
      number5: selectedPhotos.length,
      photoNum: allPhotoCount
    })

    if (!vipInfo.isVip) {
      payment.open('webBatchEditWatermarkOnceCnt')
      setDownloadingAll(false)
      return
    }

    if (watermarkCnt < selectedPhotos.length) {
      handleBatchTimesLimit(
        `编辑次数不足，需要${selectedPhotos.length}次，当前剩余${watermarkCnt}次`
      )
      setDownloadingAll(false)
      return
    }

    const editedCount = selectedPhotos.filter(
      (photo) =>
        photo.watermarkStatus === WatermarkExtractionStatus.EDIT_COMPLETED
    ).length

    if (editedCount > 200) {
      Modal.confirm({
        title: '提示',
        content: (
          <div>
            <p>
              你当前已编辑数量为{editedCount}
              张。为了避免异常报错，建议您下载完成后清空已编辑的照片列表。
            </p>
          </div>
        ),
        okText: '下载并移出照片列表',
        cancelText: '仅下载',
        onOk: () => {
          executeDownload(selectedPhotos, true)
        },
        onCancel: () => {
          executeDownload(selectedPhotos, false)
        }
      })
      return
    }

    executeDownload(selectedPhotos, false)
  }

  // 向上文注册下载全部回调，供根/确认离开弹窗等触发
  useEffect(() => {
    registerDownloadAllHandler(handleDownloadAll)
    return () => registerDownloadAllHandler(null)
  }, [handleDownloadAll, registerDownloadAllHandler])

  // 执行下载逻辑的函数
  const executeDownload = async (
    processedPhotos: PhotoItem[],
    removeAfterDownload: boolean
  ): Promise<void> => {
    try {
      if (processedPhotos.length === 0) {
        message.error('没有可下载的已处理照片')
        setDownloadingAll(false)
        return
      }

      // 对于编辑完成的照片使用aiURL，对于无修改项和超时的照片使用原图URL
      const processedUrls = processedPhotos.map((photo) => {
        const shouldUseOriginal =
          photo.watermarkStatus === WatermarkExtractionStatus.EDIT_NO_CHANGES ||
          photo.watermarkStatus === WatermarkExtractionStatus.EDIT_TIMEOUT
        return shouldUseOriginal
          ? photo.smallURL || photo.url
          : photo.aiURL || photo.smallURL || photo.url
      })
      // 获取原始URL列表，用于提取文件名

      // 显示下载进度弹窗（假的进度条，用于轮询过程）
      setDownloadProgress({
        visible: true,
        current: 0,
        total: 100
      })

      // 调用接口获取无蒙层水印的照片（新格式：返回ZIP文件URL）
      const zipUrl = await removeWatermarks(processedUrls, (current, total) => {
        // 更新假的进度条
        setDownloadProgress({
          visible: true,
          current,
          total
        })
      })

      if (!zipUrl) {
        // 去水印失败
        setDownloadProgress({
          visible: false,
          current: 0,
          total: 0
        })
        message.error('获取去水印照片失败')
        setDownloadingAll(false)
        return
      }

      // 下载ZIP文件
      try {
        const response = await fetch(zipUrl)
        if (!response.ok) {
          throw new Error(`下载失败: ${response.statusText}`)
        }

        const blob = await response.blob()

        // 生成ZIP文件名（使用日期时间）
        const now = new Date()
        const dateTimeStr = `${now.getFullYear()}${String(
          now.getMonth() + 1
        ).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(
          now.getHours()
        ).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(
          now.getSeconds()
        ).padStart(2, '0')}`
        const zipFileName = `批量编辑水印照片_${dateTimeStr}.zip`

        // 创建下载链接
        const blobUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = zipFileName
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(blobUrl)

        // 更新进度条为完成状态
        setDownloadProgress({
          visible: true,
          current: 100,
          total: 100
        })

        // 下载完成，延迟关闭弹窗以便用户看到完成状态
        setTimeout(() => {
          setDownloadProgress({
            visible: false,
            current: 0,
            total: 0
          })
        }, 1000)

        // 计算需要扣减的次数（只统计未下载的照片）
        const photosWithStatus = processedPhotos.map((photo) => ({
          url: photo.aiURL || photo.smallURL || photo.url,
          isDownloaded: photo.isDownloaded
        }))
        const notDownloadedCount = photosWithStatus.filter(
          (photo) => !photo.isDownloaded
        ).length

        // 记录去水印次数（只记录未下载照片的数量）
        if (notDownloadedCount > 0 && groupID) {
          try {
            await dispatch(
              removeWatermarkDownload({
                groupID: groupID,
                removeType: 1, // 1 表示 ai去水印
                watermarkCount: notDownloadedCount
              })
            )
          } catch (error) {
            console.error('记录去水印次数失败:', error)
          }
        }

        message.success(`成功下载${processedPhotos.length}张照片的ZIP文件`)
      } catch (error) {
        console.error('下载ZIP文件失败:', error)
        message.error('下载ZIP文件失败，请稍后重试')
        setDownloadProgress({
          visible: false,
          current: 0,
          total: 0
        })
        setDownloadingAll(false)
        return
      }

      // 如果选择下载并移出照片列表，移除已下载的照片
      if (removeAfterDownload) {
        // 获取已下载照片的seq集合
        const downloadedPhotoSeqs = new Set(
          processedPhotos.map((photo) => photo.seq)
        )

        // 过滤掉已下载的照片
        const remainingPhotos = photos.filter(
          (photo) => !downloadedPhotoSeqs.has(photo.seq)
        )

        // 更新照片列表
        setPhotos(remainingPhotos)

        // 同步更新 Redux 中的数据
        dispatch(setPhotoPickerPhotos(remainingPhotos, 'batch-editor'))

        // 调整当前索引：如果当前照片被移除，选择第一个剩余的照片
        if (remainingPhotos.length === 0) {
          // 如果所有照片都被移除，设置为 -1
          setCurrentIndex(-1)
        } else {
          const currentPhotoSeq = photos[currentIndex]?.seq
          if (currentPhotoSeq && downloadedPhotoSeqs.has(currentPhotoSeq)) {
            // 当前照片被移除，选择第一个剩余的照片
            setCurrentIndex(0)
          } else {
            // 当前照片未被移除，需要找到它在剩余列表中的新索引
            const newIndex = remainingPhotos.findIndex(
              (photo) => photo.seq === currentPhotoSeq
            )
            if (newIndex !== -1) {
              setCurrentIndex(newIndex)
            } else {
              setCurrentIndex(0)
            }
          }
        }

        message.info(`已移除${downloadedPhotoSeqs.size}张已下载的照片`)
      }

      // 减少对应的编辑次数（只扣减未下载照片的数量）
      const photosWithStatusForDeduct = processedPhotos.map((photo) => ({
        url: photo.aiURL || photo.smallURL || photo.url,
        isDownloaded: photo.isDownloaded
      }))
      const notDownloadedCountForDeduct = photosWithStatusForDeduct.filter(
        (photo) => !photo.isDownloaded
      ).length
      if (notDownloadedCountForDeduct > 0) {
        decrementWatermarkCnt(notDownloadedCountForDeduct)
      }

      // 显示问卷弹窗（如果应该显示）
      showSurveyModal()

      // 埋点：下载成功
      const finalNotDownloadedCount = photosWithStatusForDeduct.filter(
        (photo) => !photo.isDownloaded
      ).length
      trackWatermarkBatchEditor({
        actionType: 'clickDownloadAll',
        number1: watermarkCnt - finalNotDownloadedCount,
        number2: allPhotoCount,
        number3: uneditedCount,
        number4: failedExtractionCount,
        number5: processedPhotos.length, // 要下载的照片数（处理成功照片数）
        photoNum: allPhotoCount,
        bool5: true
      })
    } catch (error) {
      console.error('下载照片失败:', error)
      message.error('下载照片失败，请稍后重试')
      // 关闭下载进度弹窗
      setDownloadProgress({
        visible: false,
        current: 0,
        total: 0
      })

      // 埋点：下载失败
      trackWatermarkBatchEditor({
        actionType: 'clickDownloadAll',
        number1: watermarkCnt,
        number2: allPhotoCount,
        number3: uneditedCount,
        number4: failedExtractionCount,
        number5: processedPhotos.length, // 要下载的照片数（处理成功照片数）
        photoNum: allPhotoCount,
        bool5: false
      })
    } finally {
      // 无论成功或失败，都重置下载状态，允许重新点击
      setDownloadingAll(false)
    }
  }

  const handleDownloadCurrent = async (): Promise<void> => {
    if (!currentPhoto) {
      message.warning('没有选择的照片可下载')
      return
    }

    // 埋点：点击下载当前照片
    trackWatermarkBatchEditor({
      actionType: 'clickDownloadCurrentPhoto',
      number1: watermarkCnt,
      number2: allPhotoCount,
      number3: uneditedCount,
      number4: failedExtractionCount,
      photoNum: allPhotoCount,
      WatermarkID: currentPhoto.watermarkID
    })

    if (!vipInfo.isVip) {
      payment.open('webBatchEditWatermarkOnceCnt')
    } else {
      // 检查照片状态：只有编辑完成、无修改项或超时的照片可以下载
      const canDownload =
        currentPhoto.watermarkStatus ===
          WatermarkExtractionStatus.EDIT_COMPLETED ||
        currentPhoto.watermarkStatus ===
          WatermarkExtractionStatus.EDIT_NO_CHANGES ||
        currentPhoto.watermarkStatus === WatermarkExtractionStatus.EDIT_TIMEOUT

      if (!canDownload) {
        message.warning('当前照片未编辑，无法下载')
        return
      }

      // 对于无修改项和超时的照片，使用原图URL；对于编辑完成的照片，使用aiURL
      const shouldUseOriginalUrl =
        currentPhoto.watermarkStatus ===
          WatermarkExtractionStatus.EDIT_NO_CHANGES ||
        currentPhoto.watermarkStatus === WatermarkExtractionStatus.EDIT_TIMEOUT

      if (watermarkCnt <= 0) {
        handleSingleTimesLimit('编辑次数不足，请充值后再试')
        return
      }

      try {
        // 获取当前照片的URL
        // 对于编辑完成的照片，使用aiURL；对于无修改项和超时的照片，使用原图URL
        const currentUrl = shouldUseOriginalUrl
          ? currentPhoto.smallURL || currentPhoto.url
          : currentPhoto.aiURL || currentPhoto.smallURL || currentPhoto.url

        if (!currentUrl) {
          message.error('照片URL不存在，无法下载')
          return
        }

        // 显示下载进度弹窗（假的进度条，用于轮询过程）
        setDownloadProgress({
          visible: true,
          current: 0,
          total: 100
        })

        // 调用接口获取无蒙层水印的照片（新格式：返回ZIP文件URL）
        const zipUrl = await removeWatermarks(
          [currentUrl],
          (current, total) => {
            // 更新假的进度条
            setDownloadProgress({
              visible: true,
              current,
              total
            })
          }
        )

        if (zipUrl) {
          // 下载ZIP文件
          try {
            const response = await fetch(zipUrl)
            if (!response.ok) {
              throw new Error(`下载失败: ${response.statusText}`)
            }

            const blob = await response.blob()

            // 生成ZIP文件名（使用日期时间）
            const now = new Date()
            const dateTimeStr = `${now.getFullYear()}${String(
              now.getMonth() + 1
            ).padStart(2, '0')}${String(now.getDate()).padStart(
              2,
              '0'
            )}_${String(now.getHours()).padStart(2, '0')}${String(
              now.getMinutes()
            ).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`
            const zipFileName = `批量编辑水印照片_${dateTimeStr}.zip`

            // 创建下载链接
            const blobUrl = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = blobUrl
            link.download = zipFileName
            link.style.display = 'none'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(blobUrl)

            // 更新进度条为完成状态
            setDownloadProgress({
              visible: true,
              current: 100,
              total: 100
            })

            // 下载完成，延迟关闭弹窗以便用户看到完成状态
            setTimeout(() => {
              setDownloadProgress({
                visible: false,
                current: 0,
                total: 0
              })
            }, 1000)

            // 记录去水印次数（如果照片未下载过）
            if (!currentPhoto.isDownloaded && groupID) {
              try {
                await dispatch(
                  removeWatermarkDownload({
                    groupID: groupID,
                    removeType: 1, // 1 表示 ai去水印
                    watermarkCount: 1
                  })
                )
              } catch (error) {
                console.error('记录去水印次数失败:', error)
              }
            }

            message.success('照片下载成功')
            // 减少对应的编辑次数
            decrementWatermarkCnt(1)

            // 显示问卷弹窗（如果应该显示）
            showSurveyModal()

            // 埋点：下载成功
            trackWatermarkBatchEditor({
              actionType: 'clickDownloadCurrentPhoto',
              number1: watermarkCnt - 1,
              number2: allPhotoCount,
              number3: uneditedCount,
              number4: failedExtractionCount,
              photoNum: allPhotoCount,
              WatermarkID: currentPhoto.watermarkID,
              bool5: true
            })
          } catch (error) {
            console.error('下载ZIP文件失败:', error)
            message.error('下载ZIP文件失败，请稍后重试')
            setDownloadProgress({
              visible: false,
              current: 0,
              total: 0
            })
          }
        } else {
          // 去水印失败
          setDownloadProgress({
            visible: false,
            current: 0,
            total: 0
          })
          message.error('获取去水印照片失败')

          // 埋点：下载失败
          trackWatermarkBatchEditor({
            actionType: 'clickDownloadCurrentPhoto',
            number1: watermarkCnt,
            number2: allPhotoCount,
            number3: uneditedCount,
            number4: failedExtractionCount,
            photoNum: allPhotoCount,
            WatermarkID: currentPhoto.watermarkID,
            bool5: false
          })
        }
      } catch (error) {
        console.error('下载照片失败:', error)
        message.error('下载照片失败，请稍后重试')
        // 关闭下载进度弹窗
        setDownloadProgress({
          visible: false,
          current: 0,
          total: 0
        })

        // 埋点：下载失败
        trackWatermarkBatchEditor({
          actionType: 'clickDownloadCurrentPhoto',
          number1: watermarkCnt,
          number2: allPhotoCount,
          number3: uneditedCount,
          number4: failedExtractionCount,
          photoNum: allPhotoCount,
          WatermarkID: currentPhoto?.watermarkID,
          bool5: false
        })
      }
    }
  }

  const handleCompareChange = (checked: boolean): void => {
    // 如果没有编辑过(aiURL)，点击开关时显示提示并阻止切换
    if (!currentPhoto?.aiURL) {
      message.info('暂未编辑修改水印')
      return
    }

    setCompareBefore(checked)

    // 埋点：对比编辑前开关变化（bool4）
    // 注意：这里不单独埋点，因为埋点要求中没有单独的对比开关埋点
    // 如果需要可以添加，但根据需求文档，bool4 只在特定 actionType 时上报
  }

  // 检查当前照片是否可以选择/预览
  const isFailed =
    currentPhoto &&
    currentPhoto.watermarkStatus === WatermarkExtractionStatus.FAILED
  const isEditFailed =
    currentPhoto &&
    currentPhoto.watermarkStatus === WatermarkExtractionStatus.EDIT_FAILED
  const isExtractingPhoto =
    currentPhoto &&
    currentPhoto.watermarkStatus === WatermarkExtractionStatus.EXTRACTING

  // 获取当前显示的图片URL（固定高分辨率宽度）
  const getDisplayImageURL = useCallback(() => {
    if (!currentPhoto) return ''

    // 对比模式开启时显示smallUrl，关闭时显示aiUrl（如果有）
    let imageUrl = ''
    if (compareBefore) {
      imageUrl = currentPhoto.smallURL || ''
    } else {
      imageUrl = currentPhoto.aiURL || currentPhoto.smallURL || ''
    }

    if (imageUrl.startsWith('data:') || imageUrl.startsWith('blob:')) {
      return imageUrl
    }

    // 统一走 OSS 固定宽度参数，降低体积
    return buildProcessedUrl(imageUrl, 1000, 85)
  }, [currentPhoto, compareBefore, buildProcessedUrl])

  useEffect(() => {
    if (!currentPhoto) {
      setIsPreviewLoading(false)
      return
    }
    setIsPreviewLoading(true)
  }, [currentPhoto?.seq, compareBefore])

  return (
    <div className="image-preview">
      <div className="image-info">
        <p className="photo-title">照片预览</p>
        <p className="photo-tip">
          编辑水印后点击&ldquo;应用&rdquo;后可预览编辑效果
          <br />
          注意：暂不支持显示高亮、主题色、透明度、无遮挡、大小等编辑和预览
        </p>
      </div>

      {!currentPhoto ? (
        <div className="empty-state">
          <div className="empty-content">
            <p>请选择一张照片编辑，编辑后可应用到全部照片</p>
          </div>
        </div>
      ) : (
        <div>
          <div
            className={`image-container${
              isPreviewLoading ? ' image-container--loading' : ''
            }`}
          >
            <img
              src={getDisplayImageURL() ?? currentPhoto.smallURL}
              alt=""
              className={`image${isPreviewLoading ? ' is-loading' : ''}`}
              loading="eager"
              decoding="async"
              onLoad={() => setIsPreviewLoading(false)}
              onError={() => setIsPreviewLoading(false)}
            />

            {/* 水印提取中的状态展示 */}
            {isExtractingPhoto && (
              <div className="extracting-overlay">
                <LoadingOutlined
                  style={{
                    fontSize: 32,
                    color: '#fff'
                  }}
                />
                <div className="extracting-text">水印提取中</div>
              </div>
            )}

            {/* 水印提取失败的遮罩展示 */}
            {isFailed && (
              <FailedOverlay
                errorMsg={currentPhoto?.errorMsg}
                defaultMsg="该照片水印提取失败,暂不支持编辑"
                reason={currentPhoto?.reason}
                taskID={currentPhoto?.taskID}
              />
            )}

            {/* 编辑失败的遮罩展示 */}
            {isEditFailed &&
              (() => {
                // 计算可编辑项的数量
                const editableItemCount = calculateEditableItemCount(
                  currentPhoto?.watermarkData
                )

                // 如果可编辑项为0，提示添加自定义项
                // 否则提示重新编辑水印
                const suggestionMsg =
                  editableItemCount === 0
                    ? '建议添加自定义项后，点击【应用到当前图片】'
                    : '建议重新编辑水印后，点击【应用到当前图片】'

                const errorMsg = currentPhoto?.errorMsg
                  ? `${currentPhoto.errorMsg}, \n${suggestionMsg}`
                  : suggestionMsg

                return (
                  <FailedOverlay
                    errorMsg={errorMsg}
                    defaultMsg="照片编辑失败，请稍后重试"
                    reason={currentPhoto?.reason}
                    taskID={currentPhoto?.taskID}
                  />
                )
              })()}
          </div>

          {processedPhotosCount > 0 && (
            <>
              <BottomToolbar
                watermarkCnt={watermarkCnt}
                onUploadClick={() => setUploadModalVisible(true)}
                onDownloadClick={() => setDownloadModalVisible(true)}
                processedCount={processedPhotosCount}
                downloadingAll={downloadingAll}
                showCompareSection={__RELEASE_STAGE__ !== 'production'}
                compareBefore={compareBefore}
                onCompareChange={handleCompareChange}
                hasCurrentAiURL={!!currentPhoto?.aiURL}
              />
              <DownloadEditedPhotosModal
                visible={downloadModalVisible}
                photos={photos}
                confirmLoading={downloadingAll}
                onCancel={() => setDownloadModalVisible(false)}
                onConfirm={(selectedPhotos) => {
                  setDownloadModalVisible(false)
                  handleDownloadSelected(selectedPhotos)
                }}
              />
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default ImagePreview
